import { useApi } from './useApi'
import { useSubscriptionStore, type UpgradeData } from '@/stores/subscription'
import { useAuthStore } from '@/stores/auth'

export interface UpgradeResponseV1 {
  success: boolean
  message: string
  subscriptionId?: string
  invoiceId?: string
  client_secret?: string
  requires_action?: boolean
}

export interface UpgradeActionResult {
  success: boolean
  data?: UpgradeResponseV1
  message?: string
  error?: string
}

export function useSubscription() {
  const api = useApi()
  const subscriptionStore = useSubscriptionStore()
  const authStore = useAuthStore()

  function normalizeUpgradeResponse(
    response: Awaited<ReturnType<typeof api.post<UpgradeResponseV1>>>,
    fallbackError = 'Upgrade failed. Please try again.',
  ): UpgradeActionResult {
    if (response.error) {
      return { success: false, error: response.error }
    }

    if (!response.data) {
      return { success: false, error: fallbackError }
    }

    const success = response.data.success === true
    const message = response.data.message

    return {
      success,
      data: response.data,
      message,
      error: success ? undefined : message || fallbackError,
    }
  }

  async function fetchUpgradePreview() {
    if (!authStore.membershipId || !authStore.productId) {
      subscriptionStore.setError('Missing membership_id or product_id')
      return { success: false, error: 'Missing required parameters' }
    }

    if (!authStore.hasValidToken) {
      subscriptionStore.setError('No valid authentication token')
      return { success: false, error: 'Authentication required' }
    }

    subscriptionStore.setLoading(true)
    subscriptionStore.setError(null)

    try {
      const response = await api.post<UpgradeData>('/user/subscriptions/upgrade-preview', {
        membershipId: authStore.membershipId,
        productId: authStore.productId,
      })

      if (response.error) {
        subscriptionStore.setError(response.error)
        return { success: false, error: response.error }
      }

      if (response.data) {
        subscriptionStore.setUpgradeData(response.data)
        return { success: true, data: response.data }
      }

      subscriptionStore.setError('No data received from server')
      return { success: false, error: 'No data received' }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      subscriptionStore.setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      subscriptionStore.setLoading(false)
    }
  }

  async function processUpgrade() {
    if (!authStore.hasValidToken) {
      return { success: false, error: 'Authentication required' }
    }

    if (!subscriptionStore.hasData) {
      return { success: false, error: 'No upgrade preview data available' }
    }

    try {
      const response = await api.post<UpgradeResponseV1>('/user/subscriptions/upgrade', {
        membershipId: authStore.membershipId,
        productId: authStore.productId,
        successUrl: authStore.successUrl,
        cancelUrl: authStore.cancelUrl,
      })

      return normalizeUpgradeResponse(response)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  async function processUpgradeWithSetup() {
    if (!authStore.hasValidToken) {
      return { success: false, error: 'Authentication required' }
    }

    if (!authStore.membershipId || !authStore.productId) {
      return { success: false, error: 'Missing membership_id or product_id' }
    }

    try {
      const response = await api.post('/user/subscriptions/upgrade-with-setup', {
        membershipId: authStore.membershipId,
        productId: authStore.productId,
      })

      if (response.error) {
        return { success: false, error: response.error }
      }

      return { success: true, data: response.data }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  async function confirmUpgradeSetup(setupIntentId: string) {
    if (!authStore.hasValidToken) {
      return { success: false, error: 'Authentication required' }
    }

    if (!authStore.membershipId || !authStore.productId) {
      return { success: false, error: 'Missing membership_id or product_id' }
    }

    if (!setupIntentId) {
      return { success: false, error: 'Setup intent ID is required' }
    }

    try {
      const response = await api.post<UpgradeResponseV1>(
        '/user/subscriptions/confirm-upgrade-setup',
        {
          setupIntentId,
          membershipId: authStore.membershipId,
          productId: authStore.productId,
        },
      )

      return normalizeUpgradeResponse(
        response,
        'Failed to confirm setup and upgrade. Please try again.',
      )
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  async function confirmUpgrade(subscriptionId: string) {
    if (!authStore.hasValidToken) {
      return { success: false, error: 'Authentication required' }
    }

    if (!subscriptionId) {
      return { success: false, error: 'Subscription ID is required' }
    }

    try {
      const response = await api.post<UpgradeResponseV1>('/user/subscriptions/confirm-upgrade', {
        subscriptionId,
      })

      return normalizeUpgradeResponse(
        response,
        'Failed to confirm subscription upgrade. Please try again.',
      )
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  // Auto-fetch when auth data becomes available
  async function initializeUpgradeData() {
    if (authStore.hasValidToken && authStore.membershipId && authStore.productId) {
      await fetchUpgradePreview()
    }
  }

  function formatAmount(amount: number, currencyCode: string = 'usd'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode.toUpperCase(),
    }).format(amount / 100) // Stripe amounts are in cents
  }

  return {
    // Actions
    fetchUpgradePreview,
    processUpgrade,
    processUpgradeWithSetup,
    confirmUpgradeSetup,
    confirmUpgrade,
    initializeUpgradeData,
    formatAmount,
  }
}
